import { z } from 'zod';
import type { CalendarUnit } from '../due.js';
import { DomainError } from '../errors.js';
import { parseLocalDate } from '../local-date.js';
import { toIssues } from '../materials/catalog.js';
import { isValidRunningHours } from '../running-hours.js';
import type { ParseResult } from '../voyage/schemas.js';
import { counterEquipmentFor } from './counters.js';
import type { EquipmentNode } from './equipment.js';

/**
 * Görev kütüphanesi: iş tanımı ve bakım aralığı (plan M7 Task 7; PMS belgesi §7–§10; ADR 0029
 * K6, K11, K12, K21). Saf modül: hata iletileri koddur, arayüz çevirir.
 */

/**
 * İş türü, yani bakım etkinliği (PMS belgesi §7; referans ekrandaki "Work Type"). İlk sekizi PMS
 * belgesinden; ardından ISO 14224 Table B.5'in eksik etkinlikleri (DNV-CP-0206 Sec.2 [4.3] f;
 * ADR 0133 §4). Sıra korunur: eski değerler yerinde kalır, yeniler sona eklenir.
 */
export const WORK_TYPES = [
  'Inspection',
  'Overhaul',
  'Service',
  'Cleaning',
  'Replace',
  'Test',
  'Calibration',
  'Lubrication',
  'Repair',
  'Modify',
  'Adjust',
  'Refit',
  'Check',
  'Combination',
  'Other',
] as const;
export type WorkType = (typeof WORK_TYPES)[number];

/** ISO 14224:2016 Table B.5 bakım etkinlikleri. */
export const ISO14224_ACTIVITIES = [
  'Replace',
  'Repair',
  'Modify',
  'Adjust',
  'Refit',
  'Check',
  'Service',
  'Test',
  'Inspection',
  'Overhaul',
  'Combination',
  'Other',
] as const;
export type Iso14224Activity = (typeof ISO14224_ACTIVITIES)[number];

/**
 * İş türünün ISO 14224 etkinliği: B.5'teki değer kendisidir; PMS belgesinin ek üç türü B.5
 * tanımlarıyla eşlenir: temizlik ve yağlama "Service" (periyodik servis: temizlik, yağlama,
 * yakıt/yağ ikmali), kalibrasyon "Adjust" (parametreleri sınırlar içine getirme).
 */
export const ISO14224_ACTIVITY_OF: Readonly<Record<WorkType, Iso14224Activity>> = {
  Inspection: 'Inspection',
  Overhaul: 'Overhaul',
  Service: 'Service',
  Cleaning: 'Service',
  Replace: 'Replace',
  Test: 'Test',
  Calibration: 'Adjust',
  Lubrication: 'Service',
  Repair: 'Repair',
  Modify: 'Modify',
  Adjust: 'Adjust',
  Refit: 'Refit',
  Check: 'Check',
  Combination: 'Combination',
  Other: 'Other',
};

/** Bilinmeyen (eski) iş türü için `null`. */
export function iso14224ActivityOf(workType: string | null | undefined): Iso14224Activity | null {
  return workType && workType in ISO14224_ACTIVITY_OF
    ? ISO14224_ACTIVITY_OF[workType as WorkType]
    : null;
}

/** İşin sınıfı: önleyici, muayene, test, durum tetikli, klas sörveyi [NR496 4.4.2a]. */
export const JOB_TYPES = [
  'preventive',
  'inspection',
  'test',
  'condition_based',
  'class_survey',
] as const;
export type JobType = (typeof JOB_TYPES)[number];

export const JOB_PRIORITIES = ['low', 'medium', 'high'] as const;
export type JobPriority = (typeof JOB_PRIORITIES)[number];

/** Aralığın dayanağı: üretici, klas, deneyim, şirket [UR Z20 1.2.1]. */
export const INTERVAL_BASES = ['maker', 'class', 'experience', 'company'] as const;
export type IntervalBasis = (typeof INTERVAL_BASES)[number];

/** İşin dayandığı gerekliliğin kaynağı (PMS belgesi §18). */
export const REQUIREMENT_SOURCES = [
  'Class',
  'Flag',
  'IMO',
  'SOLAS',
  'MARPOL',
  'ISM',
  'MLC',
  'OEM',
] as const;

/** Kontrol listesi maddesi: işaretlenir ya da değer girilir. */
export const CHECKLIST_ITEM_KINDS = ['check', 'value'] as const;
export type ChecklistItemKind = (typeof CHECKLIST_ITEM_KINDS)[number];

export const CALENDAR_UNITS = [
  'day',
  'week',
  'month',
  'year',
] as const satisfies readonly CalendarUnit[];

/**
 * Klas sürekli sörvey (CMS) döngüsü: her kalem en geç 5 yılda bir incelenir; PMS aralığı bunu
 * aşamaz [UR Z18 1.3, UR Z20 1.2.1] (K11).
 */
export const CMS_MAX_MONTHS = 60;

/** Ortalama ay uzunluğu (gün): 365,25 / 12. */
const DAYS_PER_MONTH = 30.4375;

/** İşin aralığı: takvim, çalışma saati ve durum (ölçüm noktası) tetikleyicileri [NR496 4.4.2a]. */
export interface JobInterval {
  calendar: { every: number; unit: CalendarUnit } | null;
  runningHours: number | null;
  conditionPointIds: string[];
}

const missing = (input: unknown) => input === undefined || input === null;
const typed = (code: string) => ({
  error: (issue: { input?: unknown }) => (missing(issue.input) ? 'REQUIRED' : code),
});
const collapse = (value: string) => value.normalize('NFC').trim().replace(/\s+/g, ' ');

const text = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(collapse)
    .pipe(z.string().min(1, 'REQUIRED').max(max, 'TOO_LONG'));

const optionalText = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(collapse)
    .pipe(z.string().max(max, 'TOO_LONG'))
    .transform((value) => (value === '' ? null : value))
    .nullish()
    .transform((value) => value ?? null);

/** Çok satırlı metin: satır sonları korunur, boş satırlar atılır. */
const optionalLines = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform((value) =>
      value
        .normalize('NFC')
        .split(/\r\n|\r|\n/)
        .map(collapse)
        .filter((line) => line !== '')
        .join('\n'),
    )
    .pipe(z.string().max(max, 'TOO_LONG'))
    .transform((value) => (value === '' ? null : value))
    .nullish()
    .transform((value) => value ?? null);

const option = <T extends readonly [string, ...string[]]>(values: T) =>
  z.enum(values, typed('INVALID_OPTION'));

/** Kapalı nesne: tanımsız alan `UNRECOGNIZED_KEY`, nesne olmayan değer `INVALID_OBJECT`. */
const closed = <T extends z.core.$ZodLooseShape>(shape: T) =>
  z.strictObject(shape, {
    error: (issue) =>
      issue.code === 'unrecognized_keys'
        ? 'UNRECOGNIZED_KEY'
        : missing(issue.input)
          ? 'REQUIRED'
          : 'INVALID_OBJECT',
  });

const id = () => z.uuid(typed('INVALID_ID'));
const optionalId = () =>
  id()
    .nullish()
    .transform((value) => value ?? null);

const list = <T extends z.ZodType>(item: T, max: number) =>
  z
    .array(item, typed('INVALID_LIST'))
    .max(max, 'TOO_MANY')
    .nullish()
    .transform((value) => (value ?? []) as z.output<T>[]);

const hours = () =>
  z
    .number(typed('INVALID_NUMBER'))
    .positive('OUT_OF_RANGE')
    .max(1_000_000, 'OUT_OF_RANGE')
    .refine(isValidRunningHours, 'INVALID_HOURS');

const localDate = z.string(typed('INVALID_DATE')).refine((value) => {
  try {
    parseLocalDate(value);
    return true;
  } catch {
    return false;
  }
}, 'INVALID_DATE');

export const jobIntervalSchema = closed({
  calendar: closed({
    every: z
      .number(typed('INVALID_NUMBER'))
      .int('INVALID_NUMBER')
      .min(1, 'OUT_OF_RANGE')
      .max(1000, 'OUT_OF_RANGE'),
    unit: option(CALENDAR_UNITS),
  })
    .nullish()
    .transform((value) => value ?? null),
  runningHours: hours()
    .nullish()
    .transform((value) => value ?? null),
  conditionPointIds: list(id(), 20),
});

const checklistItem = closed({
  text: text(500),
  kind: option(CHECKLIST_ITEM_KINDS),
  unit: optionalText(20),
});

const requirementRef = closed({
  source: option(REQUIREMENT_SOURCES),
  reference: text(200),
});

const sparePart = closed({
  materialId: id(),
  quantity: z
    .number(typed('INVALID_NUMBER'))
    .positive('OUT_OF_RANGE')
    .max(1_000_000, 'OUT_OF_RANGE'),
});

/**
 * İş tanımı (PMS belgesi §7–§10; plan Task 7). Tanımsız alan kabul edilmez. Sayaç verilmezse
 * yazma yolu en yakın sayaçlı atayı yazar (K6).
 */
export const jobDefinitionInputSchema = closed({
  code: text(40),
  title: text(200),
  workType: option(WORK_TYPES),
  jobType: option(JOB_TYPES),
  priority: option(JOB_PRIORITIES),
  interval: jobIntervalSchema,
  intervalBasis: option(INTERVAL_BASES),
  counterEquipmentId: optionalId(),
  procedure: list(text(1000), 100),
  checklist: list(checklistItem, 100),
  safetyItems: list(text(500), 50),
  acceptanceCriteria: optionalLines(2000),
  requirementRefs: list(requirementRef, 20),
  responsibleRoleId: optionalId(),
  estimatedMinutes: z
    .number(typed('INVALID_NUMBER'))
    .int('INVALID_NUMBER')
    .min(0, 'OUT_OF_RANGE')
    .max(100_000, 'OUT_OF_RANGE')
    .nullish()
    .transform((value) => value ?? null),
  instructionDocumentId: optionalId(),
  manualDocumentId: optionalId(),
  manualRef: optionalText(200),
  plannedSpareParts: list(sparePart, 50),
  ceSurvey: z
    .boolean(typed('INVALID_OPTION'))
    .nullish()
    .transform((value) => value ?? false),
  /** UMS / E0–ECO alarm testi işi (DNV-CP-0206 Sec.2 [4.3] d; ADR 0133 §3). */
  alarmTest: z
    .boolean(typed('INVALID_OPTION'))
    .nullish()
    .transform((value) => value ?? false),
  baselineDoneOn: localDate.nullish().transform((value) => value ?? null),
  baselineDoneHours: z
    .number(typed('INVALID_NUMBER'))
    .min(0, 'OUT_OF_RANGE')
    .refine(isValidRunningHours, 'INVALID_HOURS')
    .nullish()
    .transform((value) => value ?? null),
}).superRefine((input, ctx) => {
  const issue = (path: string, message: string) =>
    ctx.addIssue({ code: 'custom', message, path: path.split('.') });
  const { calendar, runningHours, conditionPointIds } = input.interval;
  const bounded = calendar !== null || runningHours !== null;
  // En az bir tetikleyici [NR496 4.4.2a]
  if (!bounded && conditionPointIds.length === 0) issue('interval', 'INTERVAL_REQUIRED');
  // Durum tetikli işin takvim ya da saat üst aralığı zorunlu: ölçüm alınmazsa iş tavandan
  // kaçmaz (K21)
  else if (!bounded) issue('interval', 'MAX_INTERVAL_REQUIRED');
  // Önceki sistemden gelen saat, tarihi olmadan anlamsızdır (K12)
  if (input.baselineDoneHours !== null && input.baselineDoneOn === null) {
    issue('baselineDoneOn', 'BASELINE_DATE_REQUIRED');
  }
});
export type JobDefinitionInput = z.output<typeof jobDefinitionInputSchema>;

/**
 * İş tanımını ayrıştırır. `today` gemi sunucusunun yerel günüdür: önceki sistemden gelen son
 * yapılış gelecekte olamaz (CLAUDE.md "Zaman").
 */
export function parseJobDefinitionInput(
  input: unknown,
  options: { today?: string } = {},
): ParseResult<JobDefinitionInput> {
  const result = jobDefinitionInputSchema.safeParse(input);
  if (!result.success) return { ok: false, issues: toIssues(result.error) };
  const { baselineDoneOn } = result.data;
  if (options.today !== undefined && baselineDoneOn !== null && baselineDoneOn > options.today) {
    return { ok: false, issues: [{ path: 'baselineDoneOn', code: 'FUTURE_DATE' }] };
  }
  return { ok: true, value: result.data };
}

/** Takvim aralığının ay eşdeğeri (gün ve hafta ortalama ay uzunluğuyla). */
export function calendarMonths(calendar: { every: number; unit: CalendarUnit }): number {
  switch (calendar.unit) {
    case 'day':
      return calendar.every / DAYS_PER_MONTH;
    case 'week':
      return (calendar.every * 7) / DAYS_PER_MONTH;
    case 'month':
      return calendar.every;
    case 'year':
      return calendar.every * 12;
  }
}

/**
 * Saat aralığının ay eşdeğeri: sayaç günlük üst sınırıyla (K5) çalışırsa aralık kaç ayda dolar.
 * En hızlı kullanımla hesaplanır; gerçek kullanım daha yavaşsa aralık daha da uzundur.
 */
export function hoursEquivalentMonths(hours: number, maxDailyHours: number): number {
  return hours / (maxDailyHours * DAYS_PER_MONTH);
}

export interface IntervalRuleContext {
  /**
   * Klas kalemi: ekipman klasla ilgili işaretli, iş klas sörveyi ya da başmühendis sörveyi
   * (K11, K31).
   */
  classItem: boolean;
  /** İşin sayacı var mı (kendisi ya da sayaçlı ata; K6). */
  hasCounter: boolean;
  /** Sayacın günlük üst sınırı (K5). */
  maxDailyHours: number;
}

/**
 * Aralığın kuralları (K6, K11): saat bazlı aralık sayaç ister; klas kaleminde takvim aralığı
 * 60 ayı aşamaz; takvim üst sınırı olmayan saat aralığı 60 aydan uzun eşdeğerdeyse dayanağı
 * üretici olmalıdır [UR Z18 1.3, UR Z20 1.2.1]. Uzatma talebine de uygulanır.
 */
export function assertIntervalRules(
  interval: Pick<JobInterval, 'calendar' | 'runningHours'> & {
    conditionPointIds?: readonly string[];
  },
  basis: IntervalBasis,
  context: IntervalRuleContext,
): void {
  if (interval.runningHours !== null && !context.hasCounter) {
    throw new DomainError('COUNTER_REQUIRED');
  }
  if (!context.classItem) return;
  if (interval.calendar !== null && calendarMonths(interval.calendar) > CMS_MAX_MONTHS) {
    throw new DomainError(
      'CLASS_INTERVAL_EXCEEDS_CMS',
      `${interval.calendar.every} ${interval.calendar.unit}`,
    );
  }
  if (
    interval.calendar === null &&
    interval.runningHours !== null &&
    hoursEquivalentMonths(interval.runningHours, context.maxDailyHours) > CMS_MAX_MONTHS &&
    basis !== 'maker'
  ) {
    throw new DomainError('INTERVAL_BASIS_MAKER_REQUIRED', String(interval.runningHours));
  }
}

/**
 * Sayaç seçimi (K6): ekipmanın kendisi ya da sayaçlı atalarından biri. Başka dalın sayacı
 * seçilemez (`COUNTER_NOT_ANCESTOR`).
 */
export function assertCounterChoice(
  tree: readonly EquipmentNode[],
  equipmentId: string,
  counterId: string,
): void {
  const byId = new Map(tree.map((n) => [n.id, n]));
  const seen = new Set<string>();
  let cursor = byId.get(equipmentId);
  while (cursor && !seen.has(cursor.id)) {
    if (cursor.id === counterId) {
      if (cursor.hasCounter) return;
      break;
    }
    seen.add(cursor.id);
    cursor = cursor.parentId === null ? undefined : byId.get(cursor.parentId);
  }
  throw new DomainError('COUNTER_NOT_ANCESTOR', counterId);
}

/** Varsayılan sayaç: ekipmanın kendisi ya da sayaçlı en yakın atası (K6). */
export const defaultJobCounter = counterEquipmentFor;

const calendarDays = (calendar: JobInterval['calendar']) =>
  calendar === null ? null : calendarMonths(calendar) * DAYS_PER_MONTH;

/** İki aralık aynı mı (birimden bağımsız: 12 ay = 1 yıl). */
export function sameInterval(
  a: Pick<JobInterval, 'calendar' | 'runningHours'> & { conditionPointIds?: readonly string[] },
  b: Pick<JobInterval, 'calendar' | 'runningHours'> & { conditionPointIds?: readonly string[] },
): boolean {
  const points = (value: readonly string[] | undefined) => [...(value ?? [])].sort().join(',');
  const days = (value: JobInterval['calendar']) => {
    const result = calendarDays(value);
    return result === null ? null : Math.round(result * 1000) / 1000;
  };
  return (
    days(a.calendar) === days(b.calendar) &&
    (a.runningHours ?? null) === (b.runningHours ?? null) &&
    points(a.conditionPointIds) === points(b.conditionPointIds)
  );
}

/**
 * Aralık değişikliğinin yönü (K11): tetikleyicilerden biri uzarsa ya da kaldırılırsa uzatma
 * (`extend`), yoksa kısaltma (`shorten`). Biri kısalıp öteki uzarsa uzatmadır: onay bekler.
 * Aralık aynıysa `undefined`.
 */
export function intervalChange(
  previous: Pick<JobInterval, 'calendar' | 'runningHours'> & {
    conditionPointIds?: readonly string[];
  },
  next: Pick<JobInterval, 'calendar' | 'runningHours'> & { conditionPointIds?: readonly string[] },
): 'extend' | 'shorten' | undefined {
  if (sameInterval(previous, next)) return undefined;
  const longer = (before: number | null, after: number | null) =>
    before !== null && (after === null || after > before + 1e-9);
  const removedPoint = (previous.conditionPointIds ?? []).some(
    (point) => !(next.conditionPointIds ?? []).includes(point),
  );
  const extend =
    longer(calendarDays(previous.calendar), calendarDays(next.calendar)) ||
    longer(previous.runningHours, next.runningHours) ||
    removedPoint;
  return extend ? 'extend' : 'shorten';
}

/** İş tanımının değişiklik kuralına giren alanları. */
export interface JobChangeSnapshot {
  equipmentId: string;
  interval: JobInterval;
  baselineDoneOn: string | null;
  baselineDoneHours: number | null;
  counterEquipmentId: string | null;
  changeReason: string | null;
}

/**
 * İş tanımı değişikliğinin kuralı (K11, K12):
 * - İşin ekipmanı değişmez (`JOB_EQUIPMENT_FIXED`): kayıtlar o ekipmana bağlıdır.
 * - İptal edilmemiş kaydı ve aralık değişikliği yokken aralık doğrudan düzeltilir; gerekçe
 *   zorunludur. Varken aralık yalnız talep ile (`job_interval_changes`) değişir.
 * - Kayıt varken başlangıç değeri ve sayaç değişmez: vade saati o sayacın birikmiş toplamıdır.
 */
export function assertJobChange(
  previous: JobChangeSnapshot,
  next: JobChangeSnapshot,
  context: { hasRecords: boolean; hasIntervalChanges: boolean },
): void {
  if (previous.equipmentId !== next.equipmentId) {
    throw new DomainError('JOB_EQUIPMENT_FIXED', next.equipmentId);
  }
  if (!sameInterval(previous.interval, next.interval)) {
    if (context.hasRecords || context.hasIntervalChanges) {
      throw new DomainError('INTERVAL_CHANGE_REQUIRES_REQUEST');
    }
    if (!next.changeReason?.trim()) throw new DomainError('INTERVAL_CHANGE_REASON_REQUIRED');
  }
  if (!context.hasRecords) return;
  if (
    previous.baselineDoneOn !== next.baselineDoneOn ||
    previous.baselineDoneHours !== next.baselineDoneHours
  ) {
    throw new DomainError('BASELINE_LOCKED');
  }
  if (previous.counterEquipmentId !== next.counterEquipmentId) {
    throw new DomainError('COUNTER_LOCKED');
  }
}
